import React, { useState, useEffect } from 'react';
import './StudentAssignments.css';
import { API_ENDPOINTS } from '../config/api';

const StudentAssignments = ({ studentId }) => {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAssignments();
  }, [studentId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/student/${studentId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch assignments');
      }

      const data = await response.json();
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssignment = (assignment) => {
    setSelectedAssignment(assignment);
  };

  const getAssignmentStatus = (assignment) => {
    if (assignment.submitted) {
      return {
        status: 'completed',
        text: `Completed - Score: ${assignment.score}/${assignment.maxScore} (${assignment.percentage}%)`,
        color: '#28a745'
      };
    } else if (assignment.isOverdue) {
      return {
        status: 'overdue',
        text: 'Overdue - Submit as soon as possible',
        color: '#dc3545'
      };
    } else {
      return {
        status: 'pending',
        text: 'Not started',
        color: '#ffc107'
      };
    }
  };

  if (loading) {
    return <div className="loading">Loading assignments...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="student-assignments">
      <div className="assignments-header">
        <h2>My Assignments</h2>
        <div className="assignments-stats">
          <span className="stat">
            Total: <strong>{assignments.length}</strong>
          </span>
          <span className="stat">
            Completed: <strong>{assignments.filter(a => a.submitted).length}</strong>
          </span>
          <span className="stat">
            Pending: <strong>{assignments.filter(a => !a.submitted && !a.isOverdue).length}</strong>
          </span>
          <span className="stat overdue">
            Overdue: <strong>{assignments.filter(a => a.isOverdue && !a.submitted).length}</strong>
          </span>
        </div>
      </div>

      <div className="assignments-list">
        {assignments.length === 0 ? (
          <div className="no-assignments">
            <p>You don't have any assignments yet.</p>
            <p>Your teacher will assign work to you soon!</p>
          </div>
        ) : (
          assignments.map(assignment => {
            const statusInfo = getAssignmentStatus(assignment);
            return (
              <div key={assignment.id} className={`assignment-card ${statusInfo.status}`}>
                <div className="assignment-info">
                  <h3>{assignment.title}</h3>
                  <p className="assignment-description">{assignment.description}</p>
                  <div className="assignment-details">
                    <span className="subject">Subject: {assignment.subjectName}</span>
                    <span className="questions-count">
                      {assignment.totalQuestions} questions, {assignment.totalPoints} points
                    </span>
                    {assignment.dueDate && (
                      <span className={`due-date ${assignment.isOverdue ? 'overdue' : ''}`}>
                        Due: {new Date(assignment.dueDate).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="assignment-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: statusInfo.color }}
                    >
                      {statusInfo.text}
                    </span>
                  </div>
                </div>
                <div className="assignment-actions">
                  {!assignment.submitted ? (
                    <button
                      className={`start-assignment-btn ${assignment.isOverdue ? 'overdue' : ''}`}
                      onClick={() => handleStartAssignment(assignment)}
                    >
                      {assignment.isOverdue ? 'Submit Late' : 'Start Assignment'}
                    </button>
                  ) : (
                    <div className="completed-info">
                      <div className="score-display">
                        <span className="score">{assignment.score}/{assignment.maxScore}</span>
                        <span className="percentage">({assignment.percentage}%)</span>
                      </div>
                      <div className="submitted-date">
                        Submitted: {new Date(assignment.submittedAt).toLocaleString()}
                      </div>
                      <button
                        className="review-assignment-btn"
                        onClick={() => handleStartAssignment(assignment)}
                      >
                        Review Assignment
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {selectedAssignment && (
        <AssignmentAttemptModal
          assignment={selectedAssignment}
          studentId={studentId}
          onClose={() => setSelectedAssignment(null)}
          onAssignmentSubmitted={fetchAssignments}
        />
      )}
    </div>
  );
};

// Assignment Attempt Modal Component
const AssignmentAttemptModal = ({ assignment, studentId, onClose, onAssignmentSubmitted }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  useEffect(() => {
    if (!assignment.submitted) {
      setStartTime(Date.now());
      const timer = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setIsReviewMode(true);
      // Load existing submission for review
      loadExistingSubmission();
    }
  }, [assignment.submitted]);

  const loadExistingSubmission = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/${assignment.id}/submission/${studentId}`);
      if (response.ok) {
        const submission = await response.json();
        setAnswers(submission.answers);
        setTimeSpent(submission.timeSpent);
        setSubmissionResult(submission);
      }
    } catch (error) {
      console.error('Error loading submission:', error);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < assignment.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unansweredQuestions = assignment.questions.filter(q => !answers[q.questionId]);
    if (unansweredQuestions.length > 0 && !window.confirm(`You have ${unansweredQuestions.length} unanswered questions. Submit anyway?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/${assignment.id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId,
          studentName: 'Student Name', // TODO: Get from student data
          answers,
          timeSpent
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit assignment');
      }

      const result = await response.json();
      setSubmissionResult(result.submission);
      setShowResults(true);
      onAssignmentSubmitted();
    } catch (error) {
      console.error('Error submitting assignment:', error);
      alert('Failed to submit assignment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return Math.round(((currentQuestionIndex + 1) / assignment.questions.length) * 100);
  };

  const currentQuestion = assignment.questions[currentQuestionIndex];

  if (showResults && submissionResult) {
    return (
      <div className="modal-overlay">
        <div className="modal-content results-modal">
          <div className="modal-header">
            <h3>Assignment Results</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="results-content">
            <div className="score-summary">
              <div className="final-score">
                <h2>{submissionResult.score}/{submissionResult.maxScore}</h2>
                <p className="percentage">{submissionResult.percentage}%</p>
              </div>
              <div className="score-details">
                <div className="detail-item">
                  <span>Time Spent:</span>
                  <span>{Math.round(submissionResult.timeSpent / 60)} minutes</span>
                </div>
                <div className="detail-item">
                  <span>Submitted:</span>
                  <span>{new Date(submissionResult.submittedAt).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="question-review">
              <h4>Question Review</h4>
              {assignment.questions.map((question, index) => {
                const studentAnswer = submissionResult.gradedAnswers?.find(g => g.questionId === question.questionId);
                const isCorrect = studentAnswer?.isCorrect;

                return (
                  <div key={question.questionId} className={`question-result ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="question-header">
                      <span className="question-number">Question {index + 1}</span>
                      <span className="question-score">
                        {studentAnswer?.points || 0}/{question.points || 1} points
                      </span>
                    </div>
                    <div className="question-content"
                         dangerouslySetInnerHTML={{__html: question.questionText}} />
                    <div className="answer-summary">
                      <div className="student-answer">
                        <strong>Your answer:</strong> {studentAnswer?.studentAnswer || 'Not answered'}
                      </div>
                      <div className="correct-answer">
                        <strong>Correct answer:</strong> {question.correctAnswer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="results-actions">
              <button className="close-results-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content assignment-attempt-modal">
        <div className="modal-header">
          <div className="assignment-title">
            <h3>{assignment.title}</h3>
            <span className="question-counter">
              Question {currentQuestionIndex + 1} of {assignment.questions.length}
            </span>
          </div>
          <div className="assignment-timer">
            <span className="time-spent">Time: {formatTime(timeSpent)}</span>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>

        <div className="question-content">
          <div className="question-text"
               dangerouslySetInnerHTML={{__html: currentQuestion.questionText}} />

          <div className="question-options">
            {currentQuestion.options.map((option, index) => (
              <label key={index} className="option-item">
                <input
                  type="radio"
                  name={`question-${currentQuestion.questionId}`}
                  value={option}
                  checked={answers[currentQuestion.questionId] === option}
                  onChange={(e) => handleAnswerChange(currentQuestion.questionId, e.target.value)}
                  disabled={isReviewMode}
                />
                <span className="option-text">{option}</span>
              </label>
            ))}
          </div>

          {isReviewMode && submissionResult && (
            <div className="review-info">
              <div className="your-answer">
                <strong>Your answer:</strong> {answers[currentQuestion.questionId] || 'Not answered'}
              </div>
              <div className="correct-answer">
                <strong>Correct answer:</strong> {currentQuestion.correctAnswer}
              </div>
              <div className="question-result">
                {(() => {
                  const gradedAnswer = submissionResult.gradedAnswers?.find(g => g.questionId === currentQuestion.questionId);
                  return gradedAnswer?.isCorrect ? (
                    <span className="correct">? Correct</span>
                  ) : (
                    <span className="incorrect">? Incorrect</span>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        <div className="question-navigation">
          <button
            className="nav-btn prev-btn"
            onClick={prevQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </button>

          <div className="question-indicators">
            {assignment.questions.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentQuestionIndex ? 'active' : ''} ${answers[assignment.questions[index].questionId] ? 'answered' : ''}`}
                onClick={() => setCurrentQuestionIndex(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {currentQuestionIndex === assignment.questions.length - 1 ? (
            !isReviewMode && (
              <button
                className="nav-btn submit-btn"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Assignment'}
              </button>
            )
          ) : (
            <button
              className="nav-btn next-btn"
              onClick={nextQuestion}
            >
              Next
            </button>
          )}
        </div>

        {assignment.instructions && (
          <div className="assignment-instructions">
            <h4>Instructions:</h4>
            <p>{assignment.instructions}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAssignments;



